import {
  Stack,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useState } from 'react'
import COLORS from '../styles/colors'
import { HLO } from '../types/global'
import theme from '../theme'

type HeaderMenuItem = {
  label: string
  href: string
  target?: '_blank' | '_self'
}

export type HeaderMenuProps = {
  selected: string
  showMenuItems?: boolean
  role?: HLO.Role
  countryISO: string
  maxVisibleItems?: number
}

const HeaderMenu = ({
  selected,
  showMenuItems = true,
  role = 'reader',
  countryISO,
  maxVisibleItems = 4,
}: HeaderMenuProps) => {
  const { t } = useTranslation('HeaderMenu')

  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  let items: HeaderMenuItem[] = []

  if (showMenuItems) {
    if (['reader'].includes(role)) {
      items = [
        { label: 'bookshelf', href: '/bookshelf' },
        { label: 'pillars', href: '/pillars' },
        { label: 'readerfaq', href: '/bookshelf/faq' },
        { label: 'about', href: '/about' },
      ]
    }
    if (['hlo-admin'].includes(role)) {
      items = [
        { label: 'bookshelf', href: '/bookshelf' },
        { label: 'pillars', href: '/pillars' },
        { label: 'users', href: '/admin/users/readers' },
        { label: 'readings', href: '/admin/readings' },
        { label: 'debriefings', href: '/admin/debriefings' },
        { label: 'content', href: '/admin/contentmanagement/topics' },
        { label: 'orgs', href: '/admin/orgs' },
        { label: 'statistics', href: '/admin/statistics' },
      ]
    }
    if (['librarian'].includes(role)) {
      items = [
        { label: 'bookshelf', href: '/bookshelf' },
        { label: 'users', href: '/admin/users/readers' },
        { label: 'readings', href: '/admin/readings' },
        { label: 'debriefings', href: '/admin/debriefings' },
      ]
    }
    if (['local-librarian'].includes(role)) {
      items = [
        { label: 'manage registered readers', href: '/admin/users/readers' },
        { label: 'bookshelf', href: '/bookshelf' },
      ]
    }
    if (['book'].includes(role)) {
      items = [
        { label: 'dashboard', href: '/book' },
        { label: 'bookshelf', href: '/bookshelf' },
        { label: 'bookfaq', href: '/book/faq' },
        { label: 'debriefings', href: '/debriefings/' },
        {
          label: 'training',
          href:
            countryISO === 'USA'
              ? 'https://bookportal.humanlibrary.org/'
              : 'https://bogportalen.menneskebiblioteket.dk/',
          target: '_blank',
        },
      ]
    }
  }

  const visibleItems = items.slice(0, maxVisibleItems)
  const overflowItems = items.slice(maxVisibleItems)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const renderMenuItem = (item: HeaderMenuItem) => {
    const isSelected = selected === item.href
    return (
      <Link key={item.label} href={item.href} passHref legacyBehavior>
        <a
          target={item.target}
          style={{
            textDecoration: 'none',
            cursor: 'pointer',
            padding: '8px 12px',
            borderRadius: 4,
            borderBottom: isSelected
              ? `3px solid ${COLORS.darkerOrange}`
              : '3px solid transparent',
            color: COLORS.grey, // text color stays consistent, no change on selected
            fontWeight: 400,
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            transition: 'border-color 0.2s ease-in-out',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
          onClick={handleMenuClose}
        >
          {t(item.label)}
        </a>
      </Link>
    )
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        height: 56,
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {visibleItems.map(renderMenuItem)}

      {overflowItems.length > 0 && (
        <>
          <IconButton
            id="header-menu-overflow-button"
            size="small"
            onClick={handleMenuOpen}
            aria-controls={open ? 'header-menu-overflow' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            sx={{
              color: COLORS.grey,
              width: 'fit-content',
            }}
          >
            {visibleItems.length === 0 ? <MenuIcon /> : <MoreHorizIcon />}
          </IconButton>

          <Menu
            id="header-menu-overflow"
            anchorEl={anchorEl}
            aria-label={t('headerMenu.overflowMenuAriaLabel') as string}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            MenuListProps={{
              'aria-labelledby': 'header-menu-overflow-button',
            }}
          >
            {overflowItems.map((item) => (
              <MenuItem
                key={item.label}
                onClick={handleMenuClose}
                disableRipple
              >
                {renderMenuItem(item)}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  )
}

export default HeaderMenu
